import { BrowserSystemOptions, Configuration, LogLevel } from '@azure/msal-browser';
import Tenant from 'models/tenants/tenant';
import AppError from 'utils/appError';
import Config from '../Config/configService';
import logger from '../Logging/logService';

//
// Client Id's
//
export const apiClientId = Config.get('Api.ClientId') as string;
export const appClientId = Config.get('App.ClientId') as string;
export const directoryAppClientId = Config.get('DirectoryApp.ClientId') as string;

//
// MSAL Configuration
//
const browserSystemOptions: BrowserSystemOptions = {
  loggerOptions: {
    loggerCallback: (level: LogLevel, message: string, containsPii: boolean): void => {
      if (containsPii) {
        return;
      }
      switch (level) {
        case LogLevel.Warning:
        case LogLevel.Error:
          logger.error(new AppError(message));

          return;
        case LogLevel.Info:
          logger.info(message);

          return;
        case LogLevel.Verbose:
          logger.debug(message);

          return;
      }
    },
    piiLoggingEnabled: false,
  },
  windowHashTimeout: 15000,
  iframeHashTimeout: 15000,
  loadFrameTimeout: 15000,
};

export const msalConfigCommon: Configuration = {
  auth: {
    clientId: appClientId,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: Config.getAppURL(),
    postLogoutRedirectUri: Config.getAppURL() + '/logout',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: browserSystemOptions,
};

export const getMsalConfigForTenant = (tenant: Tenant): Configuration => {
  return {
    auth: {
      clientId: appClientId,
      authority: `https://login.microsoftonline.com/${tenant.id}`,
      redirectUri: Config.getAppURL(),
      postLogoutRedirectUri: Config.getAppURL() + '/logout',
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
    system: browserSystemOptions,
  };
};

//
// Scopes for different requests
//

/**
 * The scopes to request when obtaining an access token for the basic login
 */
export const loginRequest = {
  scopes: ['User.Read'],
};

/**
 * The scopes to request when obtaining an access token for the API.
 */
export const apiRequest = {
  scopes: ['offline_access', `${apiClientId}/.default`],
};

/**
 * The delegated Graph permissions of the API app registration that the API exchanges the sign-in token for (on behalf
 * of the user) to save to SharePoint and OneDrive. An administrator consents to them for the API client id; the API
 * names the same scopes (GraphManager.GraphScopeLibrary).
 */
export const graphSharePointLibraryRequest = {
  scopes: ['Sites.Read.All', 'Files.ReadWrite.All'],
};

/**
 * The Selected scopes of SharePoint (phase 28 P-D) that replace the ones above while Selected sites is on: the sites
 * granted to the API registration (ISOPlanner's pair), or at the file level the files and library folders granted to it.
 * The API names the same scopes (SharePointGraphScopes.SelectedSites and SelectedFiles).
 */
export const graphSharePointSelectedSitesRequest = {
  scopes: ['Sites.Selected', 'Lists.SelectedOperations.Selected'],
};

export const graphSharePointSelectedFilesRequest = {
  scopes: ['Files.SelectedOperations.Selected'],
};

/*
 * The page every admin consent returns to, without the leading slash (authService appends it to the app URL): the
 * redirect URI registered for admin consent on the API registration and on the directory-sync registration
 * (docs/bootstrap.md). The SharePoint consent is read there; the directory consent of Groups is passed on from there by
 * its state (scenes/Admin/Groups).
 */
export const integrationsConsentRedirectUrl = 'admin/integrations';

/*
 * The admin consent of the directory group sync: .default grants every Graph permission the directory-sync registration
 * (directoryAppClientId, phase 47) declares, which are only the application permissions of the sync, declared by the
 * bootstrap. The consent declares nothing itself.
 */
export const graphDirectoryConsentRequest = {
  scopes: ['https://graph.microsoft.com/.default'],
};

/*
 * Merge default scopes
 */
export const mergeScopes = (scopes: string[][]): string[] => {
  const mergedScopes = new Set<string>();
  scopes.forEach((scopeArray) => {
    scopeArray.forEach((scope) => mergedScopes.add(scope));
  });

  return Array.from(mergedScopes);
};
